import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector'
import { TransformNode } from '@babylonjs/core/Meshes/transformNode'
import { Tools } from '@babylonjs/core/Misc/tools'
import { Scene } from '@babylonjs/core/scene'
import { ComponentConfiguration, ComponentType, FrameConfiguration, GetConfigurationFromString, GetConfigurationString, Productline } from '@lindberg/configuration'
import { distinct } from '@lindberg/utils'
import getMaterial from './MaterialLoader'
import Bridge from './Parts/Bridge'
import Bushing from './Parts/Bushing'
import Clip from './Parts/Clip'
import Crossbar from './Parts/Crossbar'
import Front from './Parts/Front'
import Lens from './Parts/Lens'
import Nosepad from './Parts/Nosepad'
import Part from './Parts/Part'
import Temple from './Parts/Temple'
import { GetUrl, Settings } from './Settings'

export type { ComponentConfiguration, ComponentType, FrameConfiguration, Productline }
export { GetConfigurationFromString, GetConfigurationString }

export class Pair<T extends Part> extends Array<T> {
	constructor(right: T, left: T) {
		super(2)
		this[0] = right
		this[1] = left
	}

	get right(): T {
		return this[0]
	}
	get left(): T {
		return this[1]
	}

	set config(val: FrameConfiguration) {
		this.right.config = val
		this.left.config = val
	}

	get hasErrors(): boolean {
		return this.right.hasErrors || this.left.hasErrors
	}
	get errors(): string[] {
		return distinct([this.right.errors, this.left.errors].flat())
	}

	async load() {
		return Promise.allSettled([
			this.right.load(),
			this.left.load()
		])
	}

	get filename() {
		return this.left.filename
	}
	get loadedFilename() {
		return this.left.loadedFilename
	}

	swap(animated: boolean = false) {
		this.right.swap(animated)
		this.left.swap(animated)
	}

	assignMaterials() {
		this.right.assignMaterials()
		this.left.assignMaterials()
	}

	setEnabled(value: boolean) {
		this.right.setEnabled(value)
		this.left.setEnabled(value)
	}

	async compileMaterials() {
		return Promise.allSettled([
			this.right.compileMaterials(),
			this.left.compileMaterials()
		])
	}
}

export class Frame extends TransformNode {
	parts: {
		front?: Front
		bridge?: Bridge
		lenses?: Pair<Lens>
		clips?: Pair<Clip>
		temples?: Pair<Temple>
		nosepads?: Pair<Nosepad>
		bushings?: Pair<Bushing>
		crossbar?: Crossbar
	} = {}

	public configuration: FrameConfiguration
	public hasErrors: boolean = false

	public center: Vector3
	public size: Vector3

	get errors() {
		return distinct(Object.values(this.parts).filter(x => x.hasErrors).flatMap(part => part.errors))
	}

	constructor(scene: Scene, configuration: FrameConfiguration = null) {
		super('FRAME', scene)
		if (configuration) this.configuration = configuration
	}

	buildFrame() {
		if (this.configuration.isRimless) {
			this.getScene().rootNodes.filter(x => x.name == 'CROSSBAR-OUT').forEach(x => x?.dispose()) // Ugly cleanup
			this.parts.bridge = new Bridge(this)
			this.parts.lenses = new Pair(new Lens(this.parts.bridge.LensOutRight), new Lens(this.parts.bridge.LensOutLeft, true))
			this.parts.nosepads = new Pair(new Nosepad(this.parts.bridge.NosepadOutRight), new Nosepad(this.parts.bridge.NosepadOutLeft))
			this.parts.clips = new Pair(new Clip(this.parts.lenses.right.ClipOut, true), new Clip(this.parts.lenses.left.ClipOut))
			this.parts.temples = new Pair(new Temple(this.parts.clips.right.TempleOut, true), new Temple(this.parts.clips.left.TempleOut, false))
			this.parts.bushings = new Pair(new Bushing(this.parts.clips.right.TempleOut, true), new Bushing(this.parts.clips.left.TempleOut, false))
			this.parts.crossbar = new Crossbar(this)
			this.parts.bridge.rotation.x = -Tools.ToRadians(this.configuration.inclination ?? 10)
		} else {
			this.parts.front = new Front(this)
			this.parts.nosepads = new Pair(new Nosepad(this.parts.front.NosepadOutRight), new Nosepad(this.parts.front.NosepadOutLeft))
			this.parts.temples = new Pair(new Temple(this.parts.front.TempleOutRight, true), new Temple(this.parts.front.TempleOutLeft, false))
			this.parts.bushings = new Pair(new Bushing(this.parts.front.TempleOutRight, true), new Bushing(this.parts.front.TempleOutLeft, false))
		}
	}

	async load(animate: boolean = false) {
		const t = performance.now()
		let rebuild = false
		if ((this.configuration.isRimless && !this.parts.bridge) || (!this.configuration.isRimless && !this.parts.front)) {
			this.parts.front?.dispose()
			this.parts.bridge?.dispose()
			this.parts.crossbar?.dispose()
			this.parts.lenses?.forEach(x => x?.dispose())
			this.parts = {}
			this.buildFrame()
			animate = false
		}
		const loads: Promise<any>[] = []

		// Preload all materials
		const materialsToLoad = Object.keys(this.configuration.components).filter(x => x != 'CONF').map(x => this.configuration.components[x] as ComponentConfiguration).filter(x => x.color).map(x => x.color)
		if (this.configuration.requiredMaterials) materialsToLoad.push(...this.configuration.requiredMaterials)
		loads.push(...distinct(materialsToLoad).map(x => getMaterial(this.getScene(), x, this.configuration.allowErrors)))

		// console.log(distinct(materialsToLoad))

		for (const str in this.parts) {
			const p: Part | Pair<Part> = this.parts[str]
			p.config = this.configuration
		}

		if (Settings.cloneParts) {
			const singleParts = Object.keys(this.parts).map(x => this.parts[x]).filter(x => x instanceof Part) as Part[]
			loads.push(...singleParts.map(x => x.load()))

			for (const pair of Object.keys(this.parts).map(x => this.parts[x]).filter(x => x instanceof Array) as Pair<Part>[]) {
				loads.push((async () => {
					const { left, right } = pair
					if (!left.filename) return
					const url = GetUrl(left.filename + '.glb')
					const request = await fetch(url, { headers: { Accept: 'model/gltf-binary' } })
					if (!request.ok) {
						right.hasErrors = left.hasErrors = true
						throw new Error(`Failed to load ${url}`)
					}
					const blob = await request.blob()
					await Promise.all([
						left.load(new File([blob], left.filename + '.glb')),
						right.load(new File([blob], right.filename + '.glb'))
					])
				})())
			}
		} else {
			loads.push(...Object.keys(this.parts).map(x => this.parts[x].load()))
		}
		const results = await Promise.allSettled(loads)
		await this.getScene().whenReadyAsync()
		this.hasErrors = Object.keys(this.parts).findIndex(x => this.parts[x].hasErrors) > -1
		if (results.find(x => x.status == 'rejected')) {
			this.hasErrors = true
			console.log(results.filter(x => x.status == 'rejected'))
		}
		const posYFrom = this.parts.bridge?.position.y || 0
		const rotXFrom = this.parts.bridge?.rotation.x || 0

		const { min, max } = this.getHierarchyBoundingVectors(true)
		this.center = min.add(max).scale(0.5)
		this.size = max.subtract(min)
		if (this.configuration.isRimless && this.parts.bridge) {
			this.parts.bridge.rotation.x = Tools.ToRadians(this.configuration.inclination ?? 10)
		}

		for (const str in this.parts) {
			await this.parts[str].compileMaterials()
		}

		for (const str in this.parts) {
			const part = this.parts[str].left || this.parts[str]
			if (part.filename) {
				this.parts[str].setEnabled(true)
				this.parts[str].swap(animate)
				this.parts[str].assignMaterials()
			} else {
				this.parts[str].setEnabled(str == 'lenses')
			}
		}

		if (this.configuration.isRimless && this.parts.bridge) {
			this.parts.bridge.position = Vector3.Zero()
			this.parts.bridge.position = this.getCenter().negate()
			if (this.parts.crossbar) this.parts.crossbar.alignToFrame(this)
		} else if (this.parts.front) {
			this.parts.front.position = Vector3.Zero()
			this.parts.front.position = this.getCenter().negate()
		}
		this.hasErrors = Object.keys(this.parts).findIndex(x => this.parts[x].hasErrors) > -1
	}

	getCenter() {
		const bounds = this.getHierarchyBoundingVectors(true, mesh => mesh.name.startsWith('LENS') /* && mesh.name.endsWith("LEFT")*/)
		return bounds.max.add(bounds.min).scale(0.5)
	}
}
